import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { handlePrintToPDF, addTempPaddingToInputs } from "../utils/printPF";


const EPFForm11 = ({eid, data}) => {
  const { user, logout, checkAuthStatus } = useAuth();
  const printRef = useRef();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) {
      logout();
    }
  }, []);

  const [formData, setFormData] = useState({
    // Member Details
    memberName: '',
    fatherName: '',
    spouseName: '',
    parentSpouseSelection: 'father',
    dateOfBirth: '',
    gender: '',
    maritalStatus: '',
    emailId: '',
    mobileNo: '',
    
    // Previous EPF/EPS Membership
    previousEPFMember: '',
    previousEPSMember: '',
    
    // Previous Employment Details (if applicable)
    uan: '',
    previousPfNo: '',
    apCode: '',
    hydCode: '',
    estCode: '',
    extnCode: '',
    pfNo: '',
    exitDate: '',
    schemeCertificateNo: '',
    ppoNo: '',
    
    // International Worker Details
    internationalWorker: '',
    countryOfOrigin: '',
    passportNo: '',
    passportValidityFrom: '',
    passportValidityTo: '',
    
    // KYC Details
    bankAccountNo: '',
    ifscCode: '',
    aadhaarNo: '',
    panNo: '',
    
    // Undertaking
    undertakingDate: '',
    undertakingPlace: '',
    memberSignature: null,
    
    // Employer Declaration
    empMemberName: '',
    joiningDate: '',
    allottedPfNumber: '',
    allottedUAN: '',
    kycStatus: '',
    previousMemberTagged: '',
    kycApprovalStatus: '',
    employerDate: '',
    employerSignature: null,
    employerSeal: null,
    empCode: '',
    companyName: ''
  });

  const [errors, setErrors] = useState([]);
  const [signaturePreviews, setSignaturePreviews] = useState({
    memberSignature: null,
    employerSignature: null,
    employerSeal: null
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const API_BASE_URL = `${process.env.REACT_APP_PUBLIC_API_BASE_URL}/v1/emp-documnets/docs`;
  const handleFileUpload = async (docType, event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      // Create filename with eid prefix
      const serverFilename = `${!eid ? user.eid : eid}_${docType}`;
      
      // Create FormData for file upload
      const formData = new FormData();
      
      // Rename the file before uploading
      const renamedFile = new File([file], serverFilename, { type: file.type });
      formData.append('file', renamedFile);

      // Upload to API
      const response = await fetch(`${API_BASE_URL}?eid=${!eid ? user.eid : eid}`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      // Refresh the documents list to get the new URL
      // await fetchUploadedDocuments();

    } catch (error) {
      console.error('Upload error:', error);
    } finally {
      event.target.value = ''; // Reset file input
    }
  };

  const handleSignatureUpload = async (e, signatureType) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file size (100KB = 102400 bytes)
    if (file.size > 102400) {
      alert('File size must be less than 100KB. Please upload a smaller file.');
      e.target.value = '';
      return;
    }

    // Check file type
    if (!file.type.startsWith('image/')) {
      alert('Please upload an image file (PNG, JPG, etc.)');
      e.target.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setSignaturePreviews(prev => ({
        ...prev,
        [signatureType]: reader.result
      }));
      setFormData(prev => ({
        ...prev,
        [signatureType]: true
      }));
    };
    reader.readAsDataURL(file);
    await handleFileUpload(signatureType, e);
  };

  const removeSignature = (signatureType) => {
    setSignaturePreviews(prev => ({
      ...prev,
      [signatureType]: null
    }));
    setFormData(prev => ({
      ...prev,
      [signatureType]: null
    }));
  };

  const getDocsUrls = async (eid)=> {
    try {
      const response = await fetch(
        `${API_BASE_URL}/get-unused-urls?eid=${eid}`
      );

      if (!response.ok) {
        throw new Error('Failed to fetch documents');
      }

      const urlsData = await response.json();
      console.log(urlsData);
      return urlsData.urls;
    } catch (error) {
      console.error('Error fetching documents:', error);
      return null;
    }
  }

  const fetchAndSetSignatures = async (eid) => {
    try {
      const urls = await getDocsUrls(eid ? eid : user.eid); // returns an array of URLs
  
      // Define mapping between keyword and state key
      const signatureMap = {
        memberSignature: "memberSignature",
        employerSignature: "employerSignature",
        employerSeal: "employerSeal"
      };
  
      // Loop through each signature type and try to find a matching URL
      Object.entries(signatureMap).forEach(async ([key, value]) => {
        const match = urls.find(url => url.toLowerCase().includes(key.toLowerCase()));
  
        if (match) {
          // Fetch and convert to Base64 Data URL
          const response = await fetch(match);
          const blob = await response.blob();
          const reader = new FileReader();
          reader.onloadend = () => {
            setSignaturePreviews(prev => ({ 
              ...prev,
              [value]: reader.result
            }));
            setFormData(prev => ({
              ...prev,
              [value]: true
            }));
          };
          reader.readAsDataURL(blob);
        }
      });
    } catch (error) {
      console.error("Error loading signature previews:", error);
    }
  };

  const validateForm = () => {
    const validationErrors = [];

    // Member Details - all mandatory
    if (!formData.memberName.trim()) validationErrors.push('Member Name is required');
    if (formData.parentSpouseSelection === 'father' && !formData.fatherName.trim()) {
      validationErrors.push("Father's Name is required");
    }
    if (formData.parentSpouseSelection === 'spouse' && !formData.spouseName.trim()) {
      validationErrors.push("Spouse's Name is required");
    }
    if (!formData.dateOfBirth) validationErrors.push('Date of Birth is required');
    if (!formData.gender) validationErrors.push('Gender is required');
    if (!formData.maritalStatus) validationErrors.push('Marital Status is required');
    if (!formData.emailId.trim()) validationErrors.push('Email ID is required');
    if (!formData.mobileNo.trim()) validationErrors.push('Mobile Number is required');

    // EPF/EPS Membership
    if (!formData.previousEPFMember) validationErrors.push('Previous EPF Membership status is required');
    if (!formData.previousEPSMember) validationErrors.push('Previous EPS Membership status is required');

    // If previous member, validate column 9
    if (formData.previousEPFMember === 'yes' || formData.previousEPSMember === 'yes') {
      if (!formData.uan.trim()) validationErrors.push('UAN is required for previous members');
    }

    // International Worker
    if (!formData.internationalWorker) validationErrors.push('International Worker status is required');
    if (formData.internationalWorker === 'yes') {
      if (!formData.countryOfOrigin.trim()) validationErrors.push('Country of Origin is required');
      if (!formData.passportNo.trim()) validationErrors.push('Passport Number is required');
    }

    // KYC Details - all mandatory
    if (!formData.bankAccountNo.trim()) validationErrors.push('Bank Account Number is required');
    if (!formData.ifscCode.trim()) validationErrors.push('IFS Code is required');
    if (!formData.aadhaarNo.trim()) validationErrors.push('Aadhaar Number is required');

    // Undertaking
    if (!formData.undertakingDate) validationErrors.push('Undertaking Date is required');
    if (!formData.undertakingPlace.trim()) validationErrors.push('Undertaking Place is required');
    // if (!formData.memberSignature) validationErrors.push('Member Signature is required');

    // Employer Declaration
    // if (!formData.empMemberName.trim()) validationErrors.push('Employee Member Name (Employer Section) is required');
    // if (!formData.joiningDate) validationErrors.push('Joining Date is required');
    // if (!formData.allottedPfNumber.trim()) validationErrors.push('Allotted PF Number is required');
    if (!formData.employerDate) validationErrors.push('Employer Declaration Date is required');
    // if (!formData.employerSignature) validationErrors.push('Employer Signature is required');
    // if (!formData.employerSeal) validationErrors.push('Employer Seal is required');

    return validationErrors;
  };

  const handleSubmit = async (e, submitType="Final") => {
    e.preventDefault();
    
    try {
      const validationErrors = validateForm();
      
      if (validationErrors.length > 0) {
        setErrors(validationErrors);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
    
      setErrors([]);
    
      const finalData = {
        eid: !eid ? user.eid : eid, 
        applicationId: `KCL${new Date().getFullYear()}${String(Math.floor(Math.random() * 10000)).padStart(4, '0')}`, 
        formType: "EPF Form 11", 
        submissionDate: new Date().toISOString(), 
        data: formData, 
        submitType 
      };
    
      console.log('Form Data:', JSON.stringify(finalData, null, 2));
      
      await fetch(`${process.env.REACT_APP_PUBLIC_API_BASE_URL}/v1/employees/save-application`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${localStorage.getItem("access_token")}`
        },
        body: JSON.stringify(finalData),
      });
      checkAuthStatus();
      navigate("/");
    } catch (error) {
      console.error(error);
    }
  };

  const getEmployeeData = async (eid, formType) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_PUBLIC_API_BASE_URL}/v1/employees/get-application?eid=${eid}&formType=${formType}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      const data = await response.json();
      return data;
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  const extractEmployeeCore = (finalData) => {
    if (!finalData) return null;
    let data = finalData.data;

    if (!data) return null;
    return {
      memberName: `${data.formData.firstName ?? ""}${data.formData.middleName?" "+data.formData.middleName:""} ${data.formData.surname ?? ""}` || "",
      empMemberName: `${data.formData.firstName ?? ""}${data.formData.middleName?" "+data.formData.middleName:""} ${data.formData.surname ?? ""}` || "",
      fatherName: data.formData.fatherName || "",
      dateOfBirth: data.formData.dobISO || "",
      gender: data.formData.gender || "",
      maritalStatus: data.formData.maritalStatus || "",
      emailId: data.formData.email || "",
      mobileNo: data.formData.mobile || "",
      aadhaarNo: data.formData.aadhaarNo || "",
      panNo: data.formData.panNo || "",
      ifscCode: data.formData.ifscCode || "",
      bankAccountNo: data.formData.accountNumber || "",
    };
  }

    const [isSubmitted, setIsSubmitted] = useState(false);
    const fetchEmployeeData = async () => {
      try {
        // First try Joining API
        const joiningData = await getEmployeeData(user.eid, "EPF Form 11");
    
        if (joiningData?.data) {
          setFormData(joiningData.data);
          setIsSubmitted(joiningData.approved);
        } else {
          // If Joining data is empty, try Personal API
          const personalData = await getEmployeeData(user.eid, "Personal");
          setFormData(prev => ({
            ...prev,
            ...extractEmployeeCore(personalData),
            empCode: eid?eid:user.eid
          }));
        }
    
      } catch (error) {
        // If Joining API failed (e.g. 404), fallback to Personal
        try {
          const personalData = await getEmployeeData(user.eid, "Personal");
          setFormData(prev => ({
            ...prev,
            ...extractEmployeeCore(personalData),
            empCode: eid?eid:user.eid
          }));
        } catch (err2) {
          console.error("Both API calls failed:", err2);
        }
      }
    };

  useEffect(() => {
    if (!user) return;
    if(data){
      setFormData(data);
      fetchAndSetSignatures(eid?eid:user.eid);
      // Set all signature previews if they exist in data
      // const previews = {};
      // ['memberSignature', 'employerSignature', 'employerSeal'].forEach(sig => {
      //   if (data[sig]) {
      //     previews[sig] = data[sig];
      //   }
      // });
      // setSignaturePreviews(prev => ({...prev, ...previews}));
      return;
    }
    fetchAndSetSignatures(eid?eid:user.eid);
    fetchEmployeeData();
  }, []);

  return (
    
    <div className="max-w-4xl mx-auto p-6 bg-white" ref={printRef}>
      <form onSubmit={handleSubmit}>
        <div className="space-y-4">
          {/* Error Messages */}
          {errors.length > 0 && (
            <div className="bg-red-50 border border-red-400 text-red-800 px-4 py-3 rounded mb-6">
              <h3 className="font-bold mb-2">Please fix the following errors:</h3>
              <ul className="list-disc list-inside space-y-1">
                {errors.map((error, index) => (
                  <li key={index} className="text-sm">{error}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Header */}
          <div className="text-center border-2 border-black p-4 mb-6">
            <h1 className="text-lg font-bold">New Form No.11- Declaration Form</h1>
            <p className="text-sm">(To be retained by the employer for future reference)</p>
            <div className="flex justify-between items-center mt-4">
              <div className="w-16 h-16 border border-black flex items-center justify-center">
                <span className="text-xs"><img src="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQUpXG4mNxnqwQPBOtlVG2lixFqIm2h8mammQ&s" alt="" width={50} height={50} /></span>
              </div>
              <div className="flex-1 text-center">
                <h2 className="font-bold">EMPLOYEES PROVIDENT FUND ORGANIZATION</h2>
                <p className="text-sm">Employees provident funds scheme, 1952 (paragraph 34 & 57) &</p>
                <p className="text-sm">Employees pension scheme 1995 (paragraph 24)</p>
              </div>
              <div className="border border-black p-2 text-sm">
                <div>Emp Code: <input type="text" name="empCode" value={formData.empCode} onChange={handleInputChange} className="border-b w-24 text-xs" /></div>
                <div className="mt-1">Company: <input type="text" name="companyName" value={formData.companyName} onChange={handleInputChange} className="border-b w-24 text-xs" /></div>
              </div>
            </div>
            <p className="text-sm mt-2">(Declaration by a person taking up employment in any establishment on which EPF Scheme, 1952 end /of EPS1995 is applicable)</p>
          </div>

          {/* Form Fields */}
          <div className="border border-black">
            <table className="w-full border-collapse">
              <tbody>
                <tr className="border-b border-black">
                  <td className="border-r border-black p-2 w-12 text-center font-bold">1</td>
                  <td className="border-r border-black p-2 w-64">Name of the member <span className="text-red-600">*</span></td>
                  <td className="p-2">
                    <input 
                      type="text" 
                      disabled={isSubmitted}
                      name="memberName" 
                      value={formData.memberName}
                      onChange={handleInputChange}
                      className="w-full border-b border-black"
                      required
                    />
                  </td>
                </tr>
                
                <tr className="border-b border-black">
                  <td className="border-r border-black p-2 text-center font-bold">2</td>
                  <td className="border-r border-black p-2">
                    Father's Name ( ) Spouse's Name ( ) <span className="text-red-600">*</span><br />
                    <span className="text-sm">(Please Tick Whichever Is Applicable)</span>
                  </td>
                  <td className="p-2">
                    <div className="flex items-center space-x-4 mb-2">
                      <label className="flex items-center">
                        <input 
                          type="radio" 
                          disabled={isSubmitted}
                          name="parentSpouseSelection" 
                          value="father"
                          checked={formData.parentSpouseSelection === 'father'}
                          onChange={handleInputChange}
                          className="mr-1"
                        />
                        Father's Name
                      </label>
                      <label className="flex items-center">
                        <input 
                          type="radio" 
                          disabled={isSubmitted}
                          name="parentSpouseSelection" 
                          value="spouse"
                          checked={formData.parentSpouseSelection === 'spouse'}
                          onChange={handleInputChange}
                          className="mr-1"
                        />
                        Spouse's Name
                      </label>
                    </div>
                    <input 
                      type="text"
                      disabled={isSubmitted} 
                      name={formData.parentSpouseSelection === 'father' ? 'fatherName' : 'spouseName'}
                      value={formData.parentSpouseSelection === 'father' ? formData.fatherName : formData.spouseName}
                      onChange={handleInputChange}
                      className="w-full border-b border-black"
                      required
                    />
                  </td>
                </tr>

                <tr className="border-b border-black">
                  <td className="border-r border-black p-2 text-center font-bold">3</td>
                  <td className="border-r border-black p-2">Date of Birth (DD/MM/YYYY) <span className="text-red-600">*</span></td>
                  <td className="p-2">
                    <input 
                      type="date"
                      disabled={isSubmitted} 
                      name="dateOfBirth" 
                      value={formData.dateOfBirth}
                      onChange={handleInputChange}
                      className="w-full border-b border-black"
                      required
                    />
                  </td>
                </tr>

                <tr className="border-b border-black">
                  <td className="border-r border-black p-2 text-center font-bold">4</td>
                  <td className="border-r border-black p-2">Gender: ( male / Female /Transgender ) <span className="text-red-600">*</span></td>
                  <td className="p-2">
                    <div className="flex space-x-4">
                      {['Male', 'Female', 'Transgender'].map(gender => (
                        <label key={gender} className="flex items-center">
                          <input 
                            type="radio" 
                            disabled={isSubmitted} 
                            name="gender" 
                            value={gender}
                            checked={formData.gender === gender}
                            onChange={handleInputChange}
                            className="mr-1"
                            required
                          />
                          {gender.charAt(0).toUpperCase() + gender.slice(1)}
                        </label>
                      ))}
                    </div>
                  </td>
                </tr>

                <tr className="border-b border-black">
                  <td className="border-r border-black p-2 text-center font-bold">5</td>
                  <td className="border-r border-black p-2">Marital Status (married /Unmarried /widow/divorce) <span className="text-red-600">*</span></td>
                  <td className="p-2">
                    <div className="flex space-x-4">
                      {['married', 'unmarried', 'widow', 'divorce'].map(status => (
                        <label key={status} className="flex items-center">
                          <input 
                            type="radio" 
                            disabled={isSubmitted} 
                            name="maritalStatus" 
                            value={status}
                            checked={formData.maritalStatus === status}
                            onChange={handleInputChange}
                            className="mr-1"
                            required
                          />
                          {status.charAt(0).toUpperCase() + status.slice(1)}
                        </label>
                      ))}
                    </div>
                  </td>
                </tr>

                <tr className="border-b border-black">
                  <td className="border-r border-black p-2 text-center font-bold">6</td>
                  <td className="border-r border-black p-2">
                    (a)Email ID: <span className="text-red-600">*</span><br />
                    (b)Mobile No: <span className="text-red-600">*</span>
                  </td>
                  <td className="p-2">
                    <div className="space-y-2">
                      <div>
                        <label className="text-sm">(a) Email ID:</label>
                        <input 
                          type="email" 
                          name="emailId" 
                          disabled={isSubmitted} 
                          value={formData.emailId}
                          onChange={handleInputChange}
                          className="w-full border-b border-black"
                          required
                        />
                      </div>
                      <div>
                        <label className="text-sm">(b) Mobile No:</label>
                        <input 
                          type="tel" 
                          name="mobileNo" 
                          disabled={isSubmitted} 
                          value={formData.mobileNo}
                          onChange={handleInputChange}
                          className="w-full border-b border-black"
                          required
                        />
                      </div>
                    </div>
                  </td>
                </tr>

                <tr className="border-b border-black">
                  <td className="border-r border-black p-2 text-center font-bold">7*</td>
                  <td className="border-r border-black p-2">Whether earlier a member of Employees 'provident Fund Scheme 1952 <span className="text-red-600">*</span></td>
                  <td className="p-2">
                    <div className="flex space-x-4">
                      <label className="flex items-center">
                        <input 
                          type="radio" 
                          disabled={isSubmitted} 
                          name="previousEPFMember" 
                          value="yes"
                          checked={formData.previousEPFMember === 'yes'}
                          onChange={handleInputChange}
                          className="mr-1"
                          required
                        />
                        Yes
                      </label>
                      <label className="flex items-center">
                        <input 
                          type="radio" 
                          disabled={isSubmitted} 
                          name="previousEPFMember" 
                          value="no"
                          checked={formData.previousEPFMember === 'no'}
                          onChange={handleInputChange}
                          className="mr-1"
                        />
                        No
                      </label>
                    </div>
                  </td>
                </tr>

                <tr className="pdf-break border-b border-black">
                  <td className="border-r border-black p-2 text-center font-bold">8*</td>
                  <td className="border-r border-black p-2">Whether earlier a member of Employees 'Pension Scheme ,1995 <span className="text-red-600">*</span></td>
                  <td className="p-2">
                    <div className="flex space-x-4">
                      <label className="flex items-center">
                        <input 
                          type="radio" 
                          disabled={isSubmitted} 
                          name="previousEPSMember" 
                          value="yes"
                          checked={formData.previousEPSMember === 'yes'}
                          onChange={handleInputChange}
                          className="mr-1"
                          required
                        />
                        Yes
                      </label>
                      <label className="flex items-center">
                        <input 
                          type="radio"
                          disabled={isSubmitted} 
                          name="previousEPSMember" 
                          value="no"
                          checked={formData.previousEPSMember === 'no'}
                          onChange={handleInputChange}
                          className="mr-1"
                        />
                        No
                      </label>
                    </div>
                  </td>
                </tr>

                <tr className=" border-b border-black">
                  <td className="border-r border-black p-2 text-center font-bold">9</td>
                  <td colSpan="2" className="p-2">
                    <div className="font-bold mb-2">If response to any or both of (7) & (8) above is yes. MANDATORY FILL UP THE (COLUMN 9)</div>
                    <div className="grid grid-cols-1 gap-2">
                      <div>
                        <label className="text-sm">a) Universal Account Number(UAN): <span className="text-red-600">*</span></label>
                        <input 
                          type="text" 
                          disabled={isSubmitted} 
                          name="uan" 
                          value={formData.uan}
                          onChange={handleInputChange}
                          className="w-full border-b border-black"
                        />
                      </div>
                      <div>
                        <label className="text-sm">b) Previous PF a/c No:</label>
                        <div className="flex space-x-1 mt-1">
                          <input type="text" disabled={isSubmitted} name="apCode" value={formData.apCode} onChange={handleInputChange} placeholder="AP" className="w-12 border border-black text-center text-xs" />
                          <input type="text" disabled={isSubmitted} name="hydCode" value={formData.hydCode} onChange={handleInputChange} placeholder="HYD" className="w-16 border border-black text-center text-xs" />
                          <input type="text" disabled={isSubmitted} name="estCode" value={formData.estCode} onChange={handleInputChange} placeholder="EST.CODE" className="w-20 border border-black text-center text-xs" />
                          <input type="text" disabled={isSubmitted} name="extnCode" value={formData.extnCode} onChange={handleInputChange} placeholder="EXTN" className="w-16 border border-black text-center text-xs" />
                          <input type="text" disabled={isSubmitted} name="pfNo" value={formData.pfNo} onChange={handleInputChange} placeholder="PF NO." className="w-20 border border-black text-center text-xs" />
                        </div>
                      </div>
                      <div className=''>
                        <label className="text-sm">c) Date of exit from previous employment (DD/MM/YYY):</label>
                        <input 
                          type="date"
                          disabled={isSubmitted} 
                          name="exitDate" 
                          value={formData.exitDate}
                          onChange={handleInputChange}
                          className="w-full border-b border-black"
                        />
                      </div>
                      <div>
                        <label className="text-sm">d) Scheme Certificate No (if Issued):</label>
                        <input 
                          type="text"
                          disabled={isSubmitted} 
                          name="schemeCertificateNo" 
                          value={formData.schemeCertificateNo}
                          onChange={handleInputChange}
                          className="w-full border-b border-black"
                        />
                      </div>
                      <div>
                        <label className="text-sm">e) Pension Payment Order (PPO)No (if Issued):</label>
                        <input 
                          type="text"
                          disabled={isSubmitted} 
                          name="ppoNo" 
                          value={formData.ppoNo}
                          onChange={handleInputChange}
                          className="w-full border-b border-black"
                        />
                      </div>
                    </div>
                  </td>
                </tr>

                <tr className="border-b border-black">
                  <td className="border-r border-black p-2 text-center font-bold">10</td>
                  <td colSpan="2" className="p-2">
                    <div className="space-y-2">
                      <div>
                        <label className="text-sm">a) International Worker: <span className="text-red-600">*</span></label>
                        <div className="flex space-x-4 inline-block ml-2">
                          <label className="flex items-center">
                            <input 
                              type="radio"
                              disabled={isSubmitted} 
                              name="internationalWorker" 
                              value="yes"
                              checked={formData.internationalWorker === 'yes'}
                              onChange={handleInputChange}
                              className="mr-1"
                              required
                            />
                            Yes
                          </label>
                          <label className="flex items-center">
                            <input 
                              type="radio"
                              disabled={isSubmitted} 
                              name="internationalWorker" 
                              value="no"
                              checked={formData.internationalWorker === 'no'}
                              onChange={handleInputChange}
                              className="mr-1"
                            />
                            No
                          </label>
                        </div>
                      </div>
                      <div>
                        <label className="text-sm">b) If Yes, State Country Of Origin (India /Name of Other Country):</label>
                        <input 
                          type="text"
                          disabled={isSubmitted} 
                          name="countryOfOrigin" 
                          value={formData.countryOfOrigin}
                          onChange={handleInputChange}
                          className="w-full border-b border-black"
                        />
                      </div>
                      <div>
                        <label className="text-sm">c) Passport No:</label>
                        <input 
                          type="text"
                          disabled={isSubmitted} 
                          name="passportNo" 
                          value={formData.passportNo}
                          onChange={handleInputChange}
                          className="w-full border-b border-black"
                        />
                      </div>
                      <div className="flex space-x-4">
                        <div className="flex-1">
                          <label className="text-sm">d) Validity Of Passport From:</label>
                          <input 
                            type="date"
                            disabled={isSubmitted} 
                            name="passportValidityFrom" 
                            value={formData.passportValidityFrom}
                            onChange={handleInputChange}
                            className="w-full border-b border-black"
                          />
                        </div>
                        <div className="flex-1">
                          <label className="text-sm">To:</label>
                          <input 
                            type="date"
                            disabled={isSubmitted} 
                            name="passportValidityTo" 
                            value={formData.passportValidityTo}
                            onChange={handleInputChange}
                            className="w-full border-b border-black"
                          />
                        </div>
                      </div>
                    </div>
                  </td>
                </tr>

                <tr className="border-b border-black">
                  <td className="border-r border-black p-2 text-center font-bold">11</td>
                  <td colSpan="2" className="p-2">
                    <div className="font-bold mb-2">KYC Details: (attach Self attested copies of following KYCs) ** <span className="text-red-600">*</span></div>
                    <div className="space-y-2">
                      <div className="flex space-x-4">
                        <div className="flex-1">
                          <label className="text-sm">a) Bank Account No: <span className="text-red-600">*</span></label>
                          <input 
                            type="text"
                            disabled={isSubmitted} 
                            name="bankAccountNo" 
                            value={formData.bankAccountNo}
                            onChange={handleInputChange}
                            className="w-full border-b border-black"
                            required
                          />
                        </div>
                        <div className="flex-1">
                          <label className="text-sm">& IFS code: <span className="text-red-600">*</span></label>
                          <input 
                            type="text"
                            disabled={isSubmitted} 
                            name="ifscCode" 
                            value={formData.ifscCode}
                            onChange={handleInputChange}
                            className="w-full border-b border-black"
                            required
                          />
                        </div>
                      </div>
                      <div className="">
                        <label className="text-sm">b) AADHAR Number (12 Digit): <span className="text-red-600">*</span></label>
                        <input 
                          type="text"
                          disabled={isSubmitted} 
                          name="aadhaarNo" 
                          value={formData.aadhaarNo}
                          onChange={handleInputChange}
                          className="w-full border-b border-black"
                          maxLength="12"
                          required
                        />
                      </div>
                      <div>
                        <label className="text-sm">c) Permanent Account Number (PAN),If available:</label>
                        <input 
                          type="text"
                          disabled={isSubmitted} 
                          name="panNo" 
                          value={formData.panNo}
                          onChange={handleInputChange}
                          className="w-full border-b border-black"
                          maxLength="10"
                        />
                      </div>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Undertaking */}
          <div className="border border-black p-4 mt-4">
            <h3 className="font-bold text-center mb-2">UNDERTAKING</h3>
            <div className="text-sm space-y-1">
              <div>1) Certified that the Particulars are true to the best of my Knowledge</div>
              <div>2) I authorize EPFO to use my Aadhar for verification / e KYC purpose for service delivery</div>
              <div>3) Kindly transfer the funds and service details, if applicable if applicable, from the previous PF account as declared above to the present P.F Account(The Transfer Would be possible only if the identified KYC details approved by previous employer has been verified by present employer</div>
              <div>4) In case of changes In above details the same Will be intimate to employer at the earliest</div>
            </div>
            <div className="grid grid-cols-3 gap-4 mt-4">
              <div>
                <label className="text-sm">Date: <span className="text-red-600">*</span></label>
                <input 
                  type="date"
                  disabled={isSubmitted} 
                  name="undertakingDate" 
                  value={formData.undertakingDate}
                  onChange={handleInputChange}
                  className="w-full border-b border-black"
                  required
                />
              </div>
              <div>
                <label className="text-sm">Place: <span className="text-red-600">*</span></label>
                <input 
                  type="text"
                  disabled={isSubmitted} 
                  name="undertakingPlace" 
                  value={formData.undertakingPlace}
                  onChange={handleInputChange}
                  className="w-full border-b border-black"
                  required
                />
              </div>
              <div>
                <label className="text-sm">Signature of Member:</label>
                <p className="text-xs text-gray-600 mb-1">Max: 100KB</p>
                {!signaturePreviews.memberSignature ? (
                  <div className="border-2 border-dashed border-gray-300 rounded p-2">
                    <input
                      type="file"
                      disabled={isSubmitted} 
                      accept="image/*"
                      onChange={(e) => handleSignatureUpload(e, 'memberSignature')}
                      className="hidden"
                      id="member-signature-upload"
                    />
                    <label
                      htmlFor="member-signature-upload"
                      className="cursor-pointer inline-block bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 text-xs"
                    >
                      Upload
                    </label>
                  </div>
                ) : (
                  <div className="border border-gray-300 p-1">
                    <div className="flex justify-between items-start mb-1">
                      <button
                        type="button"
                        disabled={isSubmitted} 
                        onClick={() => removeSignature('memberSignature')}
                        className="text-red-600 hover:text-red-800 text-xs font-medium ml-auto"
                      >
                        Remove
                      </button>
                    </div>
                    <div className="bg-gray-50 p-1 flex justify-center items-center" style={{ minHeight: '50px' }}>
                      <img
                        src={signaturePreviews.memberSignature}
                        alt="Member Signature"
                        className="max-h-12 max-w-full object-contain"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Employer Declaration */}
          <div className=" border border-black p-4 mt-4">
            <h3 className="font-bold text-center mb-2">DECLARATION BY PRESENT EMPLOYER</h3>
            <div className="space-y-4">
              <div>
                A) The member Mr./Ms./Mrs 
                <input 
                  type="text"
                  disabled={isSubmitted} 
                  name="empMemberName" 
                  value={formData.empMemberName}
                  onChange={handleInputChange}
                  className="border-b border-black mx-2 w-32"
                />
                <span className="text-red-600">*</span> has joined on 
                <input 
                  type="date"
                  disabled={isSubmitted} 
                  name="joiningDate" 
                  value={formData.joiningDate}
                  onChange={handleInputChange}
                  className="border-b border-black mx-2"
                />
                 and has been allotted PF Number
                <input 
                  type="text"
                  disabled={isSubmitted} 
                  name="allottedPfNumber" 
                  value={formData.allottedPfNumber}
                  onChange={handleInputChange}
                  className="border-b border-black mx-2 w-40"
                />
                
              </div>

              <div>
                <div className="font-bold">B) In case person was earlier not a member of EPF Scheme ,1952 and EPS,1995</div>
                <div className="ml-4">
                  (Post allotment of UAN) The UAN Allotted for the member is
                  <input 
                    type="text"
                    disabled={isSubmitted} 
                    name="allottedUAN" 
                    value={formData.allottedUAN}
                    onChange={handleInputChange}
                    className="border-b border-black mx-2 w-32"
                  />
                </div>
                <div className="pdf-break ml-4 mt-2">
                  <div className="text-sm">Please tick the Appropriate Option:</div>
                  <div className="ml-4 space-y-1">
                    <label className="flex items-center">
                      <input 
                        type="radio"
                        disabled={isSubmitted} 
                        name="kycStatus" 
                        value="not_uploaded"
                        checked={formData.kycStatus === 'not_uploaded'}
                        onChange={handleInputChange}
                        className="mr-2"
                      />
                      The KYC details of the above member in the UAN database Have not been uploaded
                    </label>
                    <label className="flex items-center">
                      <input 
                        type="radio"
                        disabled={isSubmitted} 
                        name="kycStatus" 
                        value="uploaded_not_approved"
                        checked={formData.kycStatus === 'uploaded_not_approved'}
                        onChange={handleInputChange}
                        className="mr-2"
                      />
                      Have been uploaded but not approved
                    </label>
                    <label className="flex items-center">
                      <input 
                        type="radio"
                        disabled={isSubmitted} 
                        name="kycStatus" 
                        value="uploaded_approved"
                        checked={formData.kycStatus === 'uploaded_approved'}
                        onChange={handleInputChange}
                        className="mr-2"
                      />
                      Have been uploaded and approved with DSC
                    </label>
                  </div>
                </div>
              </div>

              <div>
                <div className="font-bold">C) In case the person was earlier a member of EPF Scheme ,1952 and EPS, 1995:</div>
                <div className="ml-4 text-sm">
                  The above PF account number /UAN of the member as mentioned in (a) above has been tagged with his /her UAN/previous member ID as declared by member
                </div>
                <div className="ml-4 mt-2">
                  <div className="text-sm">Please Tick the Appropriate Option</div>
                  <div className="ml-4 space-y-1">
                    <label className="flex items-center">
                      <input 
                        type="radio"
                        disabled={isSubmitted} 
                        name="kycApprovalStatus" 
                        value="approved_transfer_generated"
                        checked={formData.kycApprovalStatus === 'approved_transfer_generated'}
                        onChange={handleInputChange}
                        className="mr-2"
                      />
                      The KYC details of the above member in the UAN database have been approved with digital signature Certificate and transfer request has been generated on portal.
                    </label>
                    <label className="flex items-center">
                      <input 
                        type="radio"
                        disabled={isSubmitted} 
                        name="kycApprovalStatus" 
                        value="physical_claim_required"
                        checked={formData.kycApprovalStatus === 'physical_claim_required'}
                        onChange={handleInputChange}
                        className="mr-2"
                      />
                      As the DSC of establishment are not registered With EPFO the member has been informed to file physical claim (Form13) for transfer of funds from his previous establishment.
                    </label>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mt-4">
                <div>
                  <label className="text-sm">Date: <span className="text-red-600">*</span></label>
                  <input 
                    type="date"
                    disabled={isSubmitted} 
                    name="employerDate" 
                    value={formData.employerDate}
                    onChange={handleInputChange}
                    className="w-full border-b border-black"
                    required
                  />
                </div>
                <div>
                  <label className="text-sm">Signature of Employer With seal of Establishment:</label>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <p className="text-xs text-gray-600 mb-1">Signature (Max: 100KB)</p>
                      {!signaturePreviews.employerSignature ? (
                        <div className="border-2 border-dashed border-gray-300 rounded p-2">
                          <input
                            type="file"
                            disabled={isSubmitted} 
                            accept="image/*"
                            onChange={(e) => handleSignatureUpload(e, 'employerSignature')}
                            className="hidden"
                            id="employer-signature-upload"
                          />
                          <label
                            htmlFor="employer-signature-upload"
                            className="cursor-pointer inline-block bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 text-xs"
                          >
                            Upload
                          </label>
                        </div>
                      ) : (
                        <div className="border border-gray-300 p-1">
                          <div className="flex justify-between items-start mb-1">
                            <button
                              type="button"
                              disabled={isSubmitted} 
                              onClick={() => removeSignature('employerSignature')}
                              className="text-red-600 hover:text-red-800 text-xs font-medium ml-auto"
                            >
                              Remove
                            </button>
                          </div>
                          <div className="bg-gray-50 p-1 flex justify-center items-center" style={{ minHeight: '50px' }}>
                            <img
                              src={signaturePreviews.employerSignature}
                              alt="Employer Signature"
                              className="max-h-12 max-w-full object-contain"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                    <div>
                      <p className="text-xs text-gray-600 mb-1">Seal (Max: 100KB)</p>
                      {!signaturePreviews.employerSeal ? (
                        <div className="border-2 border-dashed border-gray-300 rounded p-2">
                          <input
                            type="file"
                            disabled={isSubmitted} 
                            accept="image/*"
                            onChange={(e) => handleSignatureUpload(e, 'employerSeal')}
                            className="hidden"
                            id="employer-seal-upload"
                          />
                          <label
                            htmlFor="employer-seal-upload"
                            className="cursor-pointer inline-block bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 text-xs"
                          >
                            Upload
                          </label>
                        </div>
                      ) : (
                        <div className="border border-gray-300 p-1">
                          <div className="flex justify-between items-start mb-1">
                            <button
                              type="button"
                              disabled={isSubmitted} 
                              onClick={() => removeSignature('employerSeal')}
                              className="text-red-600 hover:text-red-800 text-xs font-medium ml-auto"
                            >
                              Remove
                            </button>
                          </div>
                          <div className="bg-gray-50 p-1 flex justify-center items-center" style={{ minHeight: '50px' }}>
                            <img
                              src={signaturePreviews.employerSeal}
                              alt="Employer Seal"
                              className="max-h-12 max-w-full object-contain"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="no-print text-center mt-6">
            <button
              type="button"
              onClick={()=>addTempPaddingToInputs( ()=> handlePrintToPDF(printRef, formData.memberName) )}
              className="flex-1 mr-4 bg-gray-500 text-white py-3 px-6 rounded-md hover:bg-gray-600 transition duration-200 font-medium"
            >
              Print
            </button>
            <button 
              type="submit"
              disabled={isSubmitted} 
              className="bg-blue-600 text-white px-8 py-3 rounded hover:bg-blue-700 font-bold transition-colors"
            >
              Submit Form
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default EPFForm11;